import {
  HexaContainer,
  HexaV2Button,
  HexaV2Dialog,
  HexaV2RadioButton,
  HexaV2RadioGroup,
  HexaV2Text,
  HexaV2Textarea,
} from '@/components/hexa-v2'
import {
  OpenOrClosePocMutation,
  useOpenOrClosePoc,
} from '@/proxy/seller/hooks/useOpenOrClosePoc'
import { STORE_AVAILABILITY as dict } from '@/resources/dict'
import ALL_POC_STATUSES from '@/resources/enums/pocStatus'
import { useEffect, useState } from 'react'
import { logger } from '@/monitoring/logger'
import * as S from './styles'
import { segmentClient } from '@/analytics/segmentClient'
import { pathNameToSegmentScreenName } from '@/resources/utils/analytics'
import { OwnerSquad } from '@/analytics/generated/segment'
import { useLocation } from 'react-router-dom'

type Reasons =
  | 'DELIVERYMAN'
  | 'STOCKOUT'
  | 'ENERGY'
  | 'INTERNET'
  | 'OVERTIME'
  | 'OTHER'

type HandleSuccessfulClosingProps = {
  poc: OpenOrClosePocMutation['openOrClosePoc']['poc']
  selectedReason: Reasons
  otherReasons: string
}

type HexaV2StoreClosingDialogProps = {
  handleSuccessfulClosing: (props: HandleSuccessfulClosingProps) => void
  handleCloseModal: () => void
  show: boolean
}

const HexaV2StoreClosingDialog = ({
  handleSuccessfulClosing,
  handleCloseModal,
  show,
}: HexaV2StoreClosingDialogProps) => {
  const { pathname } = useLocation()
  const [selectedReason, setSelectedReason] = useState<Reasons | null>(null)
  const [otherReason, setOtherReason] = useState<string>('')
  const [storeClosingErrors, setStoreClosingErrors] = useState<string[]>([])
  const isOtherReason = selectedReason === 'OTHER'
  const canSubmit =
    (selectedReason !== null && !isOtherReason) ||
    (isOtherReason && otherReason.length > 0)
  const userErrors = storeClosingErrors.filter(error => !!error)

  const closePoc = useOpenOrClosePoc()

  const closeStore = () => {
    const reason = {
      reason: isOtherReason ? otherReason : selectedReason,
    }

    void closePoc.mutateAsync(
      {
        input: {
          operation: ALL_POC_STATUSES.CLOSED,
          reasons: JSON.stringify(reason),
        },
      },
      {
        onSuccess: data => {
          if (
            data?.openOrClosePoc.success &&
            data?.openOrClosePoc?.poc &&
            selectedReason
          ) {
            logger.info('closeStoreSuccess', {
              operation: ALL_POC_STATUSES.CLOSED,
              reason: selectedReason,
              otherReasons: otherReason,
              isTimeInsideWorking: data.openOrClosePoc.poc.isTimeInsideWorking,
            })
            setStoreClosingErrors([])
            handleSuccessfulClosing({
              poc: data.openOrClosePoc.poc,
              selectedReason,
              otherReasons: otherReason,
            })
          }

          if (data?.openOrClosePoc?.errors?.length) {
            logger.error('closeStoreError', {
              errors: JSON.stringify(data?.openOrClosePoc?.errors),
              operation: ALL_POC_STATUSES.CLOSED,
            })
            setStoreClosingErrors(data?.openOrClosePoc?.errors)
          }
        },
      }
    )
  }

  useEffect(() => {
    if (show) {
      segmentClient.storeClosingDialogModalViewed({
        screen_name: pathNameToSegmentScreenName(pathname),
        modal_name: 'store-closing-dialog',
        owner_squad: OwnerSquad.DtcZeOrders,
      })
    }
  }, [pathname, show])

  if (!show) return null

  return (
    <S.StoreClosingDialogWrapper>
      <HexaV2Dialog
        type="custom"
        header={dict.closeStoreModalTitleV2}
        className="store-closing-dialog"
        onHxClickClose={handleCloseModal}
      >
        <HexaContainer display="flex" styleOverrides={S.containerStyles}>
          <HexaV2Text
            textStyle="bodyMedium"
            text={dict.closeStoreCallActionV2}
          />
          <HexaV2RadioGroup
            onHxChange={({ detail }) => setSelectedReason(detail as Reasons)}
            name="store-closing-dialog"
          >
            {dict.reasons.map(reason => (
              <HexaV2RadioButton
                name="store-closing-dialog"
                key={reason.value}
                id={`store-closing-dialog-${reason.value}`}
                label={reason.label}
                value={reason.value}
                checked={selectedReason === reason.value}
              />
            ))}
          </HexaV2RadioGroup>
          {selectedReason === 'OTHER' && (
            <HexaV2Textarea
              placeholder={dict.otherReasonPlaceholder}
              value={otherReason}
              defaultValue={otherReason}
              onHxChange={({ detail }) => {
                const { value } = detail.currentTarget as HTMLTextAreaElement

                setOtherReason(value)
              }}
              resize="none"
            />
          )}

          {userErrors.length > 0 && (
            <S.ErrorContainer>
              {userErrors.map(error => (
                <HexaV2Text key={error} textStyle="bodySmall" text={error} />
              ))}
            </S.ErrorContainer>
          )}

          <HexaV2Text textStyle="bodySmall" text={dict.observationTextV2} />

          <HexaContainer display="flex" styleOverrides={S.buttonStyles}>
            <HexaV2Button variant="secondary" text={dict.cancel} size="small" />
            <HexaV2Button
              variant="primary"
              text={dict.closeStore}
              size="small"
              disabled={!canSubmit}
              loading={closePoc.isPending}
              onClick={closeStore}
            />
          </HexaContainer>
        </HexaContainer>
      </HexaV2Dialog>
    </S.StoreClosingDialogWrapper>
  )
}

export default HexaV2StoreClosingDialog
